import { watch } from 'vue'
import type { Driver } from 'driver.js'
import type { Ref } from 'vue'
import { useRoute } from '#imports'
import { matchesExpectedPath } from '~/onboarding/path'
import { shouldActivatePdlClickLock } from '~/onboarding/clickLockRules'
import type { OnboardingStep } from '~/onboarding/types'
import { useOnboardingClickLock } from '~/composables/onboarding/useOnboardingClickLock'

type DomSyncDeps = {
  activeDriver: Ref<Driver | null>
  activeTourSteps: Ref<OnboardingStep[] | null>
  pendingTeleportIndex: Ref<number | null>
  toolboxOpen: Ref<boolean>
  toolboxAnimationSyncTimer: Ref<number | null>
  clearToolboxAnimationSyncTimer: () => void
  syncNextButtonDisabledState: (stepIndex: number) => void
}

type ClickLockActions = {
  clickLock: Ref<{ active: boolean }>
  activateClickLock: () => void
  deactivateClickLock: () => void
}

/** Étapes où la toolbox met un peu de temps à s’ouvrir : le spotlight Driver doit attendre la fin de l’animation. */
const TOOLBOX_OPEN_ANIMATION_DELAY_MS = 350

function syncClickLockForCurrentPath(
  step: OnboardingStep | undefined,
  currentPath: string,
  clickLockActions: ClickLockActions
): void {
  const { clickLock, activateClickLock, deactivateClickLock } = clickLockActions
  if (shouldActivatePdlClickLock(step, currentPath)) {
    activateClickLock()
  } else if (clickLock.value.active) {
    deactivateClickLock()
  }
}

function advanceIfRequiredPathReached(
  activeDriver: Driver,
  step: OnboardingStep | undefined,
  pathNow: string,
  pendingTeleportIndex: Ref<number | null>
): void {
  if (pendingTeleportIndex.value !== null) {
    return
  }
  if (step?.requiredPathPrefix && pathNow.startsWith(step.requiredPathPrefix)) {
    // L'action demandée (navigation) est réalisée: avancer automatiquement.
    activeDriver.moveNext()
  }
}

function resolvePendingTeleport(
  activeDriver: Driver,
  pathNow: string,
  activeTourSteps: Ref<OnboardingStep[] | null>,
  pendingTeleportIndex: Ref<number | null>
): void {
  if (pendingTeleportIndex.value === null) {
    return
  }
  const targetStep = (activeTourSteps.value ?? [])[pendingTeleportIndex.value]
  const expectedPath = targetStep?.expectedPath
  if (!expectedPath || matchesExpectedPath(pathNow, expectedPath)) {
    activeDriver.moveTo(pendingTeleportIndex.value)
    pendingTeleportIndex.value = null
  }
}

function handleRouteChangeForDriverSync(pathNow: string, deps: DomSyncDeps, clickLockActions: ClickLockActions): void {
  const activeDriver = deps.activeDriver.value
  if (!activeDriver || !activeDriver.isActive()) {
    clickLockActions.deactivateClickLock()
    return
  }

  const activeIndex = activeDriver.getActiveIndex() ?? 0
  const step = (deps.activeTourSteps.value ?? [])[activeIndex]
  syncClickLockForCurrentPath(step, pathNow, clickLockActions)

  if (deps.pendingTeleportIndex.value === null) {
    deps.syncNextButtonDisabledState(activeIndex)
  }
  advanceIfRequiredPathReached(activeDriver, step, pathNow, deps.pendingTeleportIndex)
  resolvePendingTeleport(activeDriver, pathNow, deps.activeTourSteps, deps.pendingTeleportIndex)
}

function scheduleToolboxOpenAnimationSync(deps: DomSyncDeps): void {
  // La toolbox s’ouvre avec une animation: synchroniser Driver après la transition,
  // sinon le spotlight peut ne pas se positionner (ex: étape "Me situer").
  deps.toolboxAnimationSyncTimer.value = window.setTimeout(function syncDriverAfterToolboxOpenAnimation() {
    deps.toolboxAnimationSyncTimer.value = null
    const driver = deps.activeDriver.value
    if (!driver || !driver.isActive()) {
      return
    }
    driver.moveTo(driver.getActiveIndex() ?? 0)
  }, TOOLBOX_OPEN_ANIMATION_DELAY_MS)
}

function shouldSyncAfterToolboxOpenAnimation(isOpenNow: boolean, step: OnboardingStep | undefined, deps: DomSyncDeps): boolean {
  return isOpenNow && step?.openToolbox === true && deps.pendingTeleportIndex.value === null
}

function handleToolboxOpenChangeForDriverSync(isOpenNow: boolean, deps: DomSyncDeps): void {
  const activeDriver = deps.activeDriver.value
  if (!activeDriver || !activeDriver.isActive()) {
    return
  }
  const activeIndex = activeDriver.getActiveIndex() ?? 0
  deps.syncNextButtonDisabledState(activeIndex)
  const step = (deps.activeTourSteps.value ?? [])[activeIndex]
  deps.clearToolboxAnimationSyncTimer()
  if (shouldSyncAfterToolboxOpenAnimation(isOpenNow, step, deps)) {
    scheduleToolboxOpenAnimationSync(deps)
  }
}

/**
 * Synchronise l’instance Driver.js active avec les changements de route et l’ouverture
 * de la toolbox : verrouillage des clics, avancement automatique, téléportation en attente.
 */
export function useOnboardingDomSync(deps: DomSyncDeps): void {
  const route = useRoute()
  const { clickLock, activateClickLock, deactivateClickLock } = useOnboardingClickLock()
  const clickLockActions: ClickLockActions = { clickLock, activateClickLock, deactivateClickLock }

  watch(
    () => route.path,
    (pathNow) => handleRouteChangeForDriverSync(pathNow, deps, clickLockActions)
  )

  watch(
    () => deps.toolboxOpen.value,
    (isOpenNow) => handleToolboxOpenChangeForDriverSync(isOpenNow, deps)
  )
}
